import React, { useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import void_ava from 'assets/gap_avatar.svg';
import styled, { css, keyframes } from 'styled-components';
import { errorAlert } from 'ui-kit/Alert';

export const CreateMessageTool = (props) => {
  const {
    allCharacters = [],
    isEditing = false,
    avatar = '',
    name = '',
    text = '',
    onCancel = () => {

    },
    onSave = Function.prototype
  } = props;

  const [selectedCharacter, setSelectedCharacter] = useState(allCharacters[0]);
  const [newMessage, setNewMessage] = useState(text);
  const [avatarError, setAvatarError] = useState(false);

  const handleImageError = (e) => {
    e.target.src = void_ava;
    setAvatarError(true);
  };

  return (
    <Wrapper>
      <Row>
        {isEditing ?
          <div className='flex gap-2 items-center'>
            {avatar == undefined || avatar == '' ? null : (
              <AvatarWrapper hasError={avatarError}>
                <img
                  alt={'avatar'}
                  src={avatar}
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />
              </AvatarWrapper>
            )}
            <Label>{name}</Label>
          </div>
          :
          <>
            <Label>Select character</Label>
            <Listbox value={selectedCharacter} onChange={setSelectedCharacter}>
              <div className="relative mt-2">
                <ListboxButton className="relative w-full cursor-default rounded-xl bg-[rgb(19,19,19)] py-4 pl-4 pr-10 text-left text-white shadow-sm focus:outline-none focus:ring-1 focus:ring-[rgb(255,126,85)] sm:text-sm sm:leading-6">
                  <span className="block truncate text-base leading-[18px]">{selectedCharacter}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
                  </span>
                </ListboxButton>

                <ListboxOptions
                  transition
                  className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-[rgb(19,19,19)] py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                >
                  {allCharacters?.map((character) => (
                    <ListboxOption
                      key={character}
                      value={character}
                      className="group relative cursor-default select-none py-2 pl-3 pr-9 text-white data-[focus]:bg-[rgb(255,126,85)] data-[focus]:text-white"
                    >
                      <span className="block truncate font-normal group-data-[selected]:font-semibold first-letter:uppercase">{character}</span>

                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[rgb(255,126,85)] group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                        <CheckIcon aria-hidden="true" className="h-5 w-5" />
                      </span>
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </>
        }
      </Row>

      <Row>
        <Label>{isEditing ? 'Change' : 'Write'} {isEditing ? name : selectedCharacter}'s message</Label>
        <TextArea
          placeholder={`${isEditing ? name : selectedCharacter}'s message`}
          value={newMessage}
          onChange={(evt) => {
            setNewMessage(evt.target.value);
          }}
        />
      </Row>
      <ButtonPlaceholder>
        <TransparentButton onClick={() => {
          onCancel();
        }}>
          Cancel
        </TransparentButton>

        <OrangeButton onClick={async () => {
          if (newMessage?.length == 0) {
            errorAlert('Please enter a message');
            return;
          }
          if (isEditing) {
            onSave(newMessage);
          } else {
            onSave({ name: selectedCharacter, text: newMessage });
          }
        }}>
          {isEditing ? 'Save' : 'Add message'}
        </OrangeButton>
      </ButtonPlaceholder>
    </Wrapper>
  );
};

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Row = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Label = styled.span`
    font-family: Nunito Sans;
    font-size: 16px;
    font-weight: 400;
    line-height: 18px;
`;

const TextArea = styled.textarea`
  background-color: rgba(19, 19, 19, 1) !important;
  border: 1px solid transparent;
  border-radius: 8px;
  color: white;
  min-height: 120px;
  font-size: 16px;
  line-height: 18px;
  outline: none;
  font-family: Nunito Sans;
  padding: 10px;
  width: 100%;
  :focus {
    border-color: #FF7E55;
    box-shadow: none !important;
  }
  ::placeholder {
    color: rgba(105, 105, 105, 1);
  }
`;

const ButtonPlaceholder = styled.div`
    padding-top: 8px;
    width: 100%;
    display: flex;
    gap: 10px;
`;

const OrangeButton = styled.button`
    background-color: rgba(255, 126, 85, 1);
    width: 100%;
    padding: 8px 24px;
    border-radius: 100px;
    font-family: Nunito Sans;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    box-shadow: 0px 12px 40px 0px rgba(255, 126, 85, 0.12);

    @media (max-width: 400px) {
        font-size: 16px;
        line-height: 18px;
        padding: 8px 16px;
    }
`;

const TransparentButton = styled(OrangeButton)`
    background-color: transparent;
    border: 1px solid rgba(255, 126, 85, 1);
    color: rgba(255, 126, 85, 1);
    box-shadow: none;
`;

const shimmerAnimation = keyframes`
  0% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  47% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  53% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  95% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 1;
  }
  100% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 54px;
  height: 54px;
  border-radius: 100px;
  overflow: hidden;
  
  ${props => props.hasError && css`
    background: #131313;
    
    &::after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 40%,
        rgba(255, 255, 255, 0.04) 47%,
        rgba(255, 255, 255, 0.07) 50%,
        rgba(255, 255, 255, 0.04) 53%,
        transparent 60%,
        transparent 100%
      );
      transform: translate(-100%, -100%) rotate(45deg);
      animation: ${shimmerAnimation} 4s cubic-bezier(0.4, 0.0, 0.2, 1) infinite;
    }
  `}
`;